import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { lettersApi } from '@/lib/api'
import { format } from 'date-fns'
import { triggerEvent } from '@/services/tracking'
import { useConfig } from '@/hooks/useConfig'
import type { Letter } from '@/types'

export default function LettersPage() {
  const { config } = useConfig()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedTags] = useState<string[]>([])

  const { data, isLoading, error } = useQuery(
    ['letters', page, search, selectedTags, config.default_page_size],
    () =>
      lettersApi.list({
        page,
        page_size: config.default_page_size,
        search,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
      }),
    { keepPreviousData: true }
  )

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center">
          <p className="text-red-600">Failed to load letters</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-olive-900">{config.app_name}</h1>
        <Link to="/upload" className="btn-primary">
          Upload Letter
        </Link>
      </div>

      {/* Search and Filters */}
      <ArchiveSearchBar search={search} onSearchChange={setSearch} onSearch={() => setPage(1)} />

      {/* Results */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {[...Array(6)].map((_, i) => (
            <LetterCardSkeleton key={i} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {data?.items.map((letter) => (
              <LetterArchiveCard key={letter.id} letter={letter} />
            ))}
          </div>

          {data?.items.length === 0 && (
            <div className="text-center py-12">
              <p className="text-olive-600">No letters found</p>
            </div>
          )}

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <ArchivePaginationControls
              currentPage={page}
              totalPages={data.total_pages}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </div>
  )
}

function ArchiveSearchBar({
  search,
  onSearchChange,
  onSearch,
}: {
  search: string
  onSearchChange: (value: string) => void
  onSearch: () => void
}) {
  const handleSearch = () => {
    // Track search event
    if (search.trim()) {
      triggerEvent('search_performed', {
        query: search,
        page: 'letters_archive',
      })
    }
    onSearch()
  }

  return (
    <div className="card">
      <div className="flex space-x-4">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search letters..."
          className="flex-1 input-field"
        />
        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>
      </div>
    </div>
  )
}

function LetterArchiveCard({ letter }: { letter: Letter }) {
  return (
    <Link
      to={`/letters/${letter.id}`}
      className="card hover:shadow-lg transition-all hover:-translate-y-1"
    >
      {letter.file_type?.startsWith('image/') && letter.file_path && (
        <div className="h-48 -mx-6 -mt-6 mb-4 bg-olive-100 rounded-t-xl overflow-hidden">
          <img src={letter.file_path} alt={letter.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <h3 className="text-lg font-semibold text-olive-900 line-clamp-2">{letter.title}</h3>
        {(letter.ocr_status === 'pending' || letter.ocr_status === 'processing') && (
          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded whitespace-nowrap">
            Processing...
          </span>
        )}
        {letter.ocr_status === 'failed' && (
          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded whitespace-nowrap">
            OCR failed
          </span>
        )}
      </div>

      {letter.sender_info?.name && (
        <p className="text-sm text-olive-600 mb-2">From: {letter.sender_info.name}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {letter.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="text-xs px-2 py-1 bg-olive-100 text-olive-700 rounded-full">
            {tag}
          </span>
        ))}
        {letter.tags.length > 3 && (
          <span className="text-xs text-olive-500">+{letter.tags.length - 3} more</span>
        )}
      </div>

      <div className="flex justify-between items-center text-sm text-olive-500">
        <span>{format(new Date(letter.created_at), 'MMM d, yyyy')}</span>
        <div className="flex items-center space-x-3">
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            {letter.view_count}
          </span>
        </div>
      </div>
    </Link>
  )
}

function LetterCardSkeleton() {
  return (
    <div className="card animate-pulse">
      <div className="h-48 -mx-6 -mt-6 mb-4 bg-olive-100 rounded-t-xl" />
      <div className="h-6 bg-olive-100 rounded mb-2" />
      <div className="h-4 bg-olive-100 rounded w-2/3 mb-3" />
      <div className="flex space-x-2">
        <div className="h-6 w-16 bg-olive-100 rounded-full" />
        <div className="h-6 w-16 bg-olive-100 rounded-full" />
      </div>
    </div>
  )
}

function ArchivePaginationControls({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}) {
  return (
    <div className="flex justify-center items-center space-x-4 mt-8">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Previous
      </button>

      <span className="text-olive-700">
        Page {currentPage} of {totalPages}
      </span>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  )
}
